import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import { GlobeIcon } from '@repo/ui/icons'
import RadioInput from '@repo/ui/radio-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { API } from '../../../../api/apis.config'
import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../../api/countries/get-countries-list'
import { getJourneys } from '../../../../api/products/get-journeys'
import { UseFiltersResponse } from '../../../../lib/hooks/use-filters'
import { ReductionTypeEnum } from '../../../../lib/types/enums/cse/reduction-type.enum'
import { ProductTypeEnum } from '../../../../lib/types/enums/product/product-type.enum'
import { Journey, JourneyFilters } from '../../../../lib/types/models/product/journey'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import DynamicDataTable, { DynamicTableRef } from '../../../elements/dynamic-table'
import PeriodBadge from '../../../elements/period-badge'
import { getDurationInDaysString } from '../../products/utils'

type Props = {
  mode?: 'single' | 'multiple'
  tableTitle?: string
  journeyId?: number
  journeys?: Pick<Journey, 'id'>[]
  isLoading?: boolean
  onSelectJourney?: (journey: Journey) => void
  onSelectJourneys?: (journeys: Journey[]) => void
  onSubmit: () => void
  filterProps: UseFiltersResponse<JourneyFilters>
  cseCode?: string | null
  // stays already linked to this center are listed first
  centerId?: number
  // show the course hours per week of every product, not only One to One
  showHoursPerWeekForAllProducts?: boolean
}

const JourneyListSelection = ({
  mode = 'single',
  tableTitle = 'Sélectionner un séjour',
  journeyId,
  journeys,
  onSelectJourneys,
  onSelectJourney,
  onSubmit,
  filterProps,
  isLoading,
  cseCode,
  centerId,
  showHoursPerWeekForAllProducts = false
}: Props) => {
  const tableRef = useRef<DynamicTableRef<Journey>>(null)
  const { selectedFilter } = filterProps
  const [isAllSelected, setIsAllSelected] = useState(false)
  // the table search, so "select all" picks the same stays the table shows
  const [search, setSearch] = useState('')
  // ids last reported by the table, to tell a selection change made outside the table apart
  const tableSelectedIdsRef = useRef<string>()

  // the table keeps its own selection: sync it when the selected journeys change from outside
  useEffect(() => {
    if (mode !== 'multiple' || !journeys || isAllSelected) return
    const ids = journeys
      .map(({ id }) => id)
      .sort()
      .join(',')
    if (tableSelectedIdsRef.current !== undefined && ids !== tableSelectedIdsRef.current)
      tableRef.current?.setSelectedRows(journeys as Journey[])
  }, [journeys, mode, isAllSelected])

  const { data: countryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  const columns: Column<Journey>[] = [
    ...(mode === 'single'
      ? ([
          {
            accessorKey: 'id' as const,
            header: '',
            width: '20px',
            cell: ({ row: { original: record } }) => (
              <div className="flex items-center">
                <RadioInput
                  disabled={!record.date.period}
                  checked={journeyId === record.id}
                  onCheck={() => {
                    if (onSelectJourney) onSelectJourney(record)
                  }}
                />
              </div>
            )
          }
        ] as Column<Journey>[])
      : []),
    {
      accessorKey: 'date.product.country.name',
      header: 'Pays',
      enableSorting: true,
      centerCell: true,
      cellClassName: 'px-0',
      cell: ({ row: { original: record } }) => (
        <div className="flex items-center">
          <img
            src={countryList?.mapping[record.date?.product?.country.code]}
            alt={'flag'}
            className={'w-4 h-4 rounded-full'}
          />
        </div>
      )
    },
    {
      accessorKey: 'date.product.formula.title',
      header: 'Formule',
      enableSorting: true,
      cellClassName: 'px-0',
      cell: ({ row }) => (
        <div className="whitespace-pre-wrap break-words py-2 ">
          {getTranslatedName(row.original.date.product.formula)}
        </div>
      )
    },
    {
      accessorKey: 'mainOption.title',
      header: 'Thème',
      enableSorting: true,

      cell: ({ row }) => (
        <div className="whitespace-pre-wrap break-words py-2 ">
          {getTranslatedName(row.original.mainOption)}
        </div>
      )
    },
    {
      accessorKey: 'secondaryOptions',
      header: 'Option(s) facultative(s)',
      cell: ({ row }) => (
        <div className="whitespace-pre-wrap break-words py-2 ">
          {row.original.secondaryOptions.length === 0
            ? '-'
            : row.original.secondaryOptions
                .map((option) => getTranslatedName(option.option))
                .join(', ')}
        </div>
      )
    },

    {
      accessorKey: 'date.departureDate',
      header: 'Dates du séjour',
      enableSorting: true,
      cell: ({ row }) => (
        <PeriodBadge
          period={row.original.date.period}
          text={`${DateFormatter.DDMMYYYY(row.original.date.departureDate)} - ${DateFormatter.DDMMYYYY(row.original.date.returnDate)}`}
        />
      )
    },
    {
      accessorKey: 'date.durationInDays',
      header: 'Durée',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge
          variant={'warning'}
          className="whitespace-pre-wrap break-words h-fit py-2 "
          text={getDurationInDaysString(row.original.date.durationInDays)}
        />
      )
    },
    {
      accessorKey: 'places.place.name',
      header: 'Lieux de séjours',
      cell: ({ row }) => (
        <div className="whitespace-pre-wrap break-words py-2 ">
          {row.original.places
            .map((tripOption) => tripOption.place.name)
            .filter((value, index, self) => self.indexOf(value) === index)
            .join(', ')}
        </div>
      )
    },
    {
      accessorKey: 'date.product.numberOfHoursPerWeek',
      header: "# d'h de cours / sem",
      cell: ({ row }) =>
        showHoursPerWeekForAllProducts ||
        row.original.date.product.type === ProductTypeEnum.ONE_TO_ONE
          ? (row.original.date.product.numberOfHoursPerWeek ?? '-')
          : '-'
    },
    {
      accessorKey: 'places.id',
      header: 'Voyage principal',
      cell: ({ row }) => {
        const mainTrips = row.original.places
          .map((tripOption) => {
            if (!tripOption.mainTrip.hasTransport) {
              return `${TripProperties.getDepartureOrJourneyPlaceLabel(tripOption.mainTrip) || tripOption.place.name} - ${TripProperties.handleTransportMeansName(tripOption.mainTrip.transportMeans)}`
            }

            return `${TripProperties.getDepartureOrJourneyPlaceLabel(tripOption.mainTrip)} - ${TripProperties.handleTransportMeansName(tripOption.mainTrip.transportMeans)}`
          })
          .filter((value, index, self) => self.indexOf(value) === index)

        return <div className="whitespace-pre-wrap break-words py-2 ">{mainTrips.join(', ')}</div>
      }
    },
    {
      accessorKey: 'places.id',
      header: 'Voyage Secondaire',
      cell: ({ row }) => {
        const secondaryTrips = row.original.places
          .filter((tripOption) => tripOption.secondaryTrip) // Filter out entries without secondaryTrip
          .map((tripOption) => getTranslatedName(tripOption.secondaryTrip?.departure))
          .filter((value, index, self) => self.indexOf(value) === index)

        return (
          <div className="whitespace-pre-wrap break-words py-2 ">
            {secondaryTrips.length > 0 ? secondaryTrips.join(', ') : '-'}
          </div>
        )
      }
    },
    {
      accessorKey: 'totalPrice',
      header: 'Prix du séjour',
      enableSorting: true,
      cell: ({ row }) => (
        <div className=" flex flex-row items-center gap-2">
          <Badge variant="primary" text={`${row.original.totalPrice}€`} />
          {row.original.cseReduction && (
            <Badge
              variant="grey"
              borderColor={tailwindColors.grey.light.DEFAULT}
              text={`${row.original.cseReduction.reduction}${row.original.cseReduction.type === ReductionTypeEnum.PERCENTAGE ? '%' : '€'}`}
            />
          )}
        </div>
      )
    },
    {
      accessorKey: 'id',
      header: '',
      centerCell: true,
      cellClassName: 'px-0',
      cell: ({ row }) => (
        <GlobeIcon
          onClick={() => {
            window.open(`https://orion.lec.info:8043/orion-journey/${row.original.id}/`, '_blank')
          }}
          className="w-6 h-6 cursor-pointer"
          color={tailwindColors.blue.DEFAULT}
        />
      )
    },
    {
      accessorKey: 'reference',
      header: 'Référence',
      enableSorting: true,
      cell: ({ row: { original: record } }) => (
        <Typography
          variant="secondary-body"
          className="whitespace-pre-wrap break-words py-2 "
          color={tailwindColors.black.DEFAULT}
        >
          {record.reference}
        </Typography>
      )
    }
  ]

  const submitButton = (
    <Button
      loading={isLoading}
      type="button"
      variant="primary"
      disabled={
        (mode === 'single' && journeyId === undefined) ||
        (mode === 'multiple' && journeys?.length === 0)
      }
      onClick={onSubmit}
      label="Suivant"
    />
  )

  return (
    <div>
      <DynamicDataTable<Journey, JourneyFilters>
        ref={tableRef}
        headerType="light"
        title={'Tous les séjours'}
        columns={columns}
        selectable={mode === 'multiple'}
        selectedData={journeys ? { rows: journeys as Journey[], isAllSelected } : undefined}
        onSelectedDataChange={
          mode === 'multiple'
            ? async ({ isAllSelected: newIsAllSelected, rows }) => {
                tableSelectedIdsRef.current = rows
                  .map(({ id }) => id)
                  .sort()
                  .join(',')
                if (onSelectJourneys) {
                  if (newIsAllSelected && !isAllSelected) {
                    // Only fetch when transitioning to "select all" for the first time
                    setIsAllSelected(true)
                    tableRef.current?.startSelectAllLoading()
                    const { data } = await getJourneys({
                      filter: selectedFilter,
                      search: search.trim()
                    })
                    onSelectJourneys(data)
                    tableRef.current?.endSelectAllLoading()
                  } else if (!newIsAllSelected) {
                    // Deselecting all or selecting individual rows
                    setIsAllSelected(false)
                    onSelectJourneys(rows)
                  }
                }
              }
            : undefined
        }
        endpoint={API.products.getAllJourneys.url}
        extraParams={{ cseCode, centerId }}
        filters={selectedFilter}
        searchLabel={'Référence du séjour'}
        onSearchChange={setSearch}
        headerInfo={
          <div className="flex w-full items-center justify-between gap-2 mt-2">
            <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
              {tableTitle}
            </Typography>
            {submitButton}
          </div>
        }
        permissions={{ delete: false }}
      />
      <div className="h-6" />
      <div className="flex w-full justify-end mb-4">{submitButton}</div>
    </div>
  )
}

export default JourneyListSelection
